/**
 * JobCard — redesigned dashboard card with 3-column layout.
 *
 * Action-needed ring system (only 3 states get highlighted rings):
 *   RED ring:    quotes_received — parts need ordering
 *   GREEN ring:  ready_to_build  — all parts in, go build
 *   ORANGE ring: ready_to_ship   — build done, ship it
 *
 * Design jobs show: "In Design" badge + DNV badge (if parent_item_is_dnv)
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge, ProgressBar, Modal, Button } from '../common';
import { useAuth } from '../../context/AuthContext';

// Generate time options in 15-minute intervals
function generateTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      const value = `${hour}:${minute}`;
      const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const amPm = h < 12 ? 'AM' : 'PM';
      const label = `${displayHour}:${minute} ${amPm}`;
      options.push({ value, label });
    }
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

// Labor types for clock-in dropdown. V2-0 K2: testing/installation
// removed from the selectable list per Kevin's request. Historical
// TimeEntry rows with those types keep aggregating normally; the
// enum and status choices remain in the backend for those rows.
const USER_LABOR_TYPES = [
  { value: 'standard', label: 'Standard Labor' },
];

// V2-a2 (partial): NEW clock-in selections are limited to the 2
// current design categories. Only used for the selection dropdowns
// below (lines ~397, ~603) — existing TimeEntry labor_type values
// are displayed via server-provided labels elsewhere, so legacy
// entries (skid_drawing/rd/software_programming/misc_design) still
// render correctly in history views.
const ADMIN_DESIGN_LABOR_TYPES = [
  { value: 'complete_system_drawing', label: 'Complete System Drawing' },
  { value: 'plc_programming', label: 'PLC Programming' },
];

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return d; }
}

function fmtTimer(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ── Action ring config: only these 3 statuses get a highlighted ring ──
const ACTION_RINGS = {
  quotes_received: {
    ring: 'ring-[3px] ring-red-500',
    bg: 'bg-red-50',
    border: 'border-red-300',
    message: '⚠ Parts Need Ordering',
    messageColor: 'text-red-600',
  },
  ready_to_build: {
    ring: 'ring-[3px] ring-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    message: '✅ All Parts In — Ready to Build',
    messageColor: 'text-emerald-700',
  },
  ready_to_ship: {
    ring: 'ring-[3px] ring-orange-500',
    bg: 'bg-orange-50',
    border: 'border-orange-300',
    message: '📦 Ready to Ship',
    messageColor: 'text-orange-600',
  },
};

// ── Left stripe colors (subtle, always present) ──
const STRIPE_COLOR = {
  pending_bom:       'bg-gray-400',
  bom_review:        'bg-indigo-400',
  waiting_on_quotes: 'bg-amber-500',
  quotes_received:   'bg-teal-500',
  waiting_on_parts:  'bg-orange-500',
  ready_to_build:    'bg-emerald-500',
  building:          'bg-green-500',
  testing:           'bg-teal-600',
  installation:      'bg-violet-600',
  ready_to_ship:     'bg-green-700',
  shipped:           'bg-sky-500',
  cancelled:         'bg-red-400',
  in_design:         'bg-purple-500',
};

export default function JobCard({
  job,
  activeClock,
  onClockToggle,
  onPinToggle,
  dragHandleProps,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isClockedIn = activeClock?.job === job.id;
  const timerRef = useRef(null);
  const clockBtnRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [clockInDropdownOpen, setClockInDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  
  // Backdated clock-in modal
  const [backdatedModalOpen, setBackdatedModalOpen] = useState(false);
  const [backdatedForm, setBackdatedForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '08:00',
    laborType: 'standard',
  });
  const [backdatedLoading, setBackdatedLoading] = useState(false);

  // Running timer
  useEffect(() => {
    if (isClockedIn && activeClock?.clock_in_at) {
      const start = new Date(activeClock.clock_in_at).getTime();
      const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
      tick();
      timerRef.current = setInterval(tick, 1000);
      return () => clearInterval(timerRef.current);
    } else {
      setElapsed(0);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [isClockedIn, activeClock?.clock_in_at]);

  const partsPercent = parseFloat(job.percent_parts_by_lines) || 0;
  const laborPercent = parseFloat(job.percent_labor_complete) || 0;

  // V2-a2 Part D — design bar inputs. `design_percent` is server-owned
  // (see _compute_design_percent in serializers.py); null means "no
  // design activity → hide the bar". `design_hours_projected` is null
  // when no DesignPhase rows exist — that's the "completed design job
  // with no estimate" case where we render actual hours instead of a %.
  const designPercent = job.design_percent != null
    ? parseFloat(job.design_percent) : null;
  const designHoursProjected = job.design_hours_projected != null
    ? parseFloat(job.design_hours_projected) : 0;
  const designHoursActual = job.design_hours_actual != null
    ? parseFloat(job.design_hours_actual) : 0;
  const hasDesignActivity = designPercent != null;
  const showDesignHoursOnly = designPercent === 100 && designHoursProjected === 0;
  const isPinned = job.is_queue_pinned === true;
  const isFlagged = job.is_flagged === true;
  const isComplete = ['complete', 'shipped', 'archived'].includes(job.status);
  const isDesign = job.is_design_job === true;
  const isDnv = job.parent_item_is_dnv === true;

  const actionRing = ACTION_RINGS[job.status] || null;
  const stripeColor = STRIPE_COLOR[job.status] || 'bg-gray-300';

  const contactDisplay = job.contact_display || null;
  const projLate =
    job.projected_completion_date &&
    job.target_due_date &&
    job.projected_completion_date > job.target_due_date;

  // ── Warning badges (non-ring warnings) ──
  const warnings = [];
  const daysSinceUpdate = job.updated_at
    ? Math.floor((Date.now() - new Date(job.updated_at).getTime()) / 86_400_000)
    : 0;
  if (job.status === 'waiting_on_quotes' && daysSinceUpdate >= 3) {
    warnings.push({ key: 'no-response', label: `No vendor reply (${daysSinceUpdate}d)`, color: 'bg-amber-100 text-amber-700' });
  }
  if (job.status === 'waiting_on_parts' && job.projected_parts_ready_date) {
    const daysUntilParts = Math.floor(
      (new Date(job.projected_parts_ready_date).getTime() - Date.now()) / 86_400_000
    );
    if (daysUntilParts < 0) {
      warnings.push({ key: 'parts-late', label: `Parts ${Math.abs(daysUntilParts)}d late`, color: 'bg-red-100 text-red-700' });
    }
  }

  return (
    <div
      className={`flex rounded-lg overflow-hidden ${
        actionRing
          ? `${actionRing.ring} ${actionRing.bg} ${actionRing.border} border shadow-md`
          : `border border-gray-200 ${isClockedIn ? 'ring-2 ring-green-400 shadow-md' : isDesign ? 'ring-1 ring-purple-200' : 'shadow-sm'}`
      } hover:shadow-lg transition-shadow cursor-pointer`}
      onClick={() => navigate(`/jobs/${job.id}`)}
    >
      {/* ── Left color stripe ── */}
      <div className={`w-1.5 flex-shrink-0 ${stripeColor}`} />

      {/* ── Card body ── */}
      <div className="flex-1 px-4 py-3 flex flex-col gap-2">

        {/* ══════ TOP ROW: Job info + status + action message + clock ══════ */}
        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Drag handle */}
          <div
            {...dragHandleProps}
            className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="9" cy="6" r="1.5" /><circle cx="15" cy="6" r="1.5" />
              <circle cx="9" cy="12" r="1.5" /><circle cx="15" cy="12" r="1.5" />
              <circle cx="9" cy="18" r="1.5" /><circle cx="15" cy="18" r="1.5" />
            </svg>
          </div>

          {/* Pin toggle */}
          <button
            onClick={(e) => { e.stopPropagation(); onPinToggle?.(job.id); }}
            title={isPinned ? 'Pinned — click to unpin' : 'Pin this job in place'}
            className={`flex-shrink-0 transition-colors rounded p-0.5 ${
              isPinned ? 'text-amber-500 hover:text-amber-600' : 'text-gray-200 hover:text-gray-400'
            }`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          </button>

          {/* Queue position */}
          <span className="text-xs text-gray-400 font-mono w-5 text-center flex-shrink-0">
            {job.queue_position || '—'}
          </span>

          {/* Job # + type icon */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className={`text-sm ${job.job_type === 'repair' ? 'text-orange-500' : isDesign ? 'text-purple-500' : 'text-blue-500'}`}>
              {job.job_type === 'repair' ? '🔧' : isDesign ? '✏️' : '⚙️'}
            </span>
            <span className="font-extrabold text-base text-foreground tracking-tight">{job.job_number}</span>
          </div>

          {/* Status badge */}
          <StatusBadge
            status={job.status}
            className="text-[13px] px-3 py-1"
            label={
              (job.status === 'quotes_received' || job.status === 'waiting_on_quotes') && job.rfqs_total > 0
                ? `${job.status === 'quotes_received' ? 'Quotes Received' : 'Waiting on Quotes'} ${job.rfqs_responded}/${job.rfqs_total}`
                : undefined
            }
          />

          {/* In Design badge */}
          {isDesign && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700 border border-purple-200 flex-shrink-0">
              ✏️ In Design
            </span>
          )}

          {/* DNV badge */}
          {isDnv && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-300 flex-shrink-0">
              🏷 DNV 2.71
            </span>
          )}

          {/* Secondary needs pulling badge */}
          {job.needs_pulling_flag && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              Needs Pulling
            </span>
          )}

          {/* Warranty badge */}
          {job.is_warranty && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Warranty
            </span>
          )}

          {/* Warning badges */}
          {warnings.map((w) => (
            <span
              key={w.key}
              className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${w.color}`}
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {w.label}
            </span>
          ))}

          {/* BOM revert flag */}
          {isFlagged && (
            <span
              title="New parts were added or quantities increased after this job progressed — generate RFQs to cover the new lines"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-yellow-100 text-yellow-700 border border-yellow-300 flex-shrink-0"
            >
              ⚠ New parts need RFQ
            </span>
          )}

          {/* To-do count badge */}
          {job.active_todo_count > 0 && (
            <span
              title={`${job.active_todo_count} active to-do item${job.active_todo_count !== 1 ? 's' : ''}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700 border border-blue-200 flex-shrink-0"
            >
              ☐ {job.active_todo_count} To-Do{job.active_todo_count !== 1 ? 's' : ''}
            </span>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Action-needed message */}
          {actionRing && (
            <span className={`text-xs font-extrabold uppercase tracking-wide flex-shrink-0 ${actionRing.messageColor}`}>
              {actionRing.message}
            </span>
          )}

          {/* Running timer */}
          {isClockedIn && (
            <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 border border-green-200 rounded-full flex-shrink-0">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-mono font-bold text-green-700">{fmtTimer(elapsed)}</span>
            </div>
          )}

          {/* Clock button */}
          {!isComplete && (
            isClockedIn ? (
              <button
                onClick={(e) => { e.stopPropagation(); onClockToggle?.(job); }}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all flex-shrink-0 bg-red-500 text-white hover:bg-red-600 shadow-md"
              >
                <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                <span>Clock Out</span>
              </button>
            ) : (
              <div className="relative flex-shrink-0">
                <button
                  ref={clockBtnRef}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!clockInDropdownOpen && clockBtnRef.current) {
                      const rect = clockBtnRef.current.getBoundingClientRect();
                      setDropdownPos({
                        top: rect.bottom + 4,
                        right: window.innerWidth - rect.right,
                      });
                    }
                    setClockInDropdownOpen(!clockInDropdownOpen);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all bg-green-500 text-white hover:bg-green-600 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Clock In</span>
                  <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {clockInDropdownOpen && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setClockInDropdownOpen(false); }} />
                    {/* Dropdown menu - fixed position calculated from button */}
                    <div 
                      className="fixed w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50 py-1 max-h-80 overflow-y-auto"
                      style={{ top: dropdownPos.top, right: dropdownPos.right }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b sticky top-0 bg-white">Select Labor Type</div>
                      {USER_LABOR_TYPES.map((lt) => (
                        <button
                          key={lt.value}
                          onClick={() => { setClockInDropdownOpen(false); onClockToggle?.(job, lt.value); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-green-50 hover:text-green-700 transition-colors"
                        >
                          {lt.label}
                        </button>
                      ))}
                      {/* Admin design labor types — shown on ANY job (V2-0 K2/A6):
                          post-design touch-ups on non-design jobs book to
                          design without flipping the job back into design.
                          The isAdmin guard remains — non-admins never see
                          these options. */}
                      {isAdmin && (
                        <>
                          <div className="border-t my-1" />
                          <div className="px-3 py-1.5 text-xs font-semibold text-purple-600 uppercase tracking-wider">Design Labor</div>
                          {ADMIN_DESIGN_LABOR_TYPES.map((lt) => (
                            <button
                              key={lt.value}
                              onClick={() => { setClockInDropdownOpen(false); onClockToggle?.(job, lt.value); }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-700 transition-colors"
                            >
                              {lt.label}
                            </button>
                          ))}
                        </>
                      )}
                      {/* Backdated clock-in option */}
                      <div className="border-t my-1" />
                      <button
                        onClick={() => {
                          setClockInDropdownOpen(false);
                          setBackdatedForm({
                            date: new Date().toISOString().split('T')[0],
                            time: '08:00',
                            laborType: 'standard',
                          });
                          setBackdatedModalOpen(true);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 transition-colors flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Forgot to clock in...
                      </button>
                    </div>
                  </>
                )}
              </div>
            )
          )}
        </div>

        {/* ══════ MAIN CONTENT: 3-column layout ══════ */}
        <div className="flex items-stretch gap-5">

          {/* ── Column 1: Customer + Parent Item info ── */}
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-slate-800 leading-tight mb-1 truncate">
              {job.customer_name}
            </div>
            <div className="flex items-baseline gap-2 flex-wrap mb-1">
              {job.parent_item_no && (
                <span className="text-[13px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded flex-shrink-0">
                  {job.parent_item_no}
                </span>
              )}
              {job.parent_item_description && (
                <span className="text-[13px] text-slate-500 truncate">
                  {job.parent_item_description}
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-[13px] text-slate-500">
              {job.quantity != null && (
                <span>
                  <span className="text-slate-400">Qty: </span>
                  <span className="font-bold text-slate-700 text-sm">{job.quantity}</span>
                </span>
              )}
              {contactDisplay && (
                <span>
                  <span className="text-slate-400">Contact: </span>
                  <span className="font-semibold text-slate-600">{contactDisplay}</span>
                </span>
              )}
              {job.paint_color && (
                <span className="flex items-center gap-1">
                  <span className="text-slate-400">🎨</span>
                  <span className="font-semibold text-slate-700">{job.paint_color}</span>
                </span>
              )}
              {job.notes && (
                <span className="truncate max-w-[300px]" title={job.notes}>
                  <span className="text-slate-400">Note: </span>
                  <span className="text-slate-600 italic">{job.notes}</span>
                </span>
              )}
              {job.active_builders && job.active_builders.length > 0 && (
                <div className="flex items-center gap-2">
                  {job.active_builders.map((builder, idx) => (
                    <BuilderTimer key={idx} builder={builder} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Column 2: Progress bars ── */}
          <div className="flex flex-col justify-center gap-1.5 w-56 flex-shrink-0">
            {isDesign ? (
              /* Design jobs show design hours progress */
              <>
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-slate-500">Design Labor</span>
                    <span className="text-xs font-bold text-slate-700">{laborPercent.toFixed(0)}%</span>
                  </div>
                  <ProgressBar value={laborPercent} height="h-2" showLabel={false} color="purple" />
                </div>
                {/* V2-a2 Part D — accurate design-only bar (design
                    projected hours vs design labor logged), rendered
                    alongside the existing mixed-labor bar. */}
                {hasDesignActivity && (
                  <div>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-semibold text-slate-500">Design</span>
                      <span className="text-xs font-bold text-slate-700">
                        {showDesignHoursOnly
                          ? `${designHoursActual.toFixed(1)}h`
                          : `${designPercent.toFixed(0)}%`}
                      </span>
                    </div>
                    <ProgressBar value={designPercent} height="h-2" showLabel={false} color="purple" />
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-slate-500">Parts</span>
                    <span className="text-xs font-bold text-slate-700">{partsPercent.toFixed(0)}%</span>
                  </div>
                  <ProgressBar value={partsPercent} height="h-2" showLabel={false} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-slate-500">Labor</span>
                    <span className="text-xs font-bold text-slate-700">{laborPercent.toFixed(0)}%</span>
                  </div>
                  <ProgressBar value={laborPercent} height="h-2" showLabel={false} />
                </div>
                {/* V2-a2 Part D — Design bar shown on non-design jobs
                    that carry design activity (e.g. a build job with
                    post-completion design touch-ups). Hidden entirely
                    when no design phases and no design TimeEntries. */}
                {hasDesignActivity && (
                  <div>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-semibold text-slate-500">Design</span>
                      <span className="text-xs font-bold text-slate-700">
                        {showDesignHoursOnly
                          ? `${designHoursActual.toFixed(1)}h`
                          : `${designPercent.toFixed(0)}%`}
                      </span>
                    </div>
                    <ProgressBar value={designPercent} height="h-2" showLabel={false} color="purple" />
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Column 3: Dates ── */}
          <div className="flex items-center gap-5 flex-shrink-0">
            {!isDesign ? (
              <>
                <div className="text-center min-w-[80px]">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Parts Ready</div>
                  <div className="text-lg font-extrabold text-slate-800 leading-tight">
                    {fmtDate(job.projected_parts_ready_date)}
                  </div>
                </div>
                <div className="text-center min-w-[80px]">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Proj. Complete</div>
                  {/* V2-b Stage B — when projected_completion_date is
                      NULL and the job is active, render a provenance
                      chip instead of blank. 'awaiting_design' takes
                      priority; anything else with a NULL date becomes
                      "Needs estimate". Persisted date wins otherwise. */}
                  {job.projected_completion_date ? (
                    <div className={`text-lg font-extrabold leading-tight ${projLate ? 'text-red-600' : 'text-slate-800'}`}>
                      {fmtDate(job.projected_completion_date)}
                    </div>
                  ) : !isComplete && job.completion_projection_source === 'awaiting_design' ? (
                    <div className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-purple-100 text-purple-700">
                      Awaiting design
                    </div>
                  ) : !isComplete ? (
                    <div className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700">
                      Needs estimate
                    </div>
                  ) : (
                    <div className="text-lg font-extrabold text-slate-400 leading-tight">—</div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="text-center min-w-[80px]">
                  <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Design Due</div>
                  <div className="text-lg font-extrabold text-slate-800 leading-tight">
                    {fmtDate(job.target_due_date)}
                  </div>
                </div>
                {/* V2-a2 Part D — Stage D projected design completion.
                    Rendered only when scheduling_v2_enabled has run
                    and produced a date (source: phases/historical). */}
                {job.projected_design_completion_date && (
                  <div className="text-center min-w-[100px]">
                    <div className="text-[11px] font-semibold text-purple-400 uppercase tracking-wide mb-0.5">Design Done</div>
                    <div className="text-lg font-extrabold text-purple-700 leading-tight">
                      ~{fmtDate(job.projected_design_completion_date)}
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="text-center min-w-[70px]">
              <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-0.5">Due Date</div>
              <div className="text-[15px] font-bold text-slate-600 leading-tight">
                {fmtDate(job.target_due_date)}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Backdated Clock-In Modal */}
      <Modal open={backdatedModalOpen} onClose={() => setBackdatedModalOpen(false)} title="Clock In (Backdated)" maxWidth="max-w-md">
        <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
            Use this if you forgot to clock in earlier.
          </div>

          {/* Date and time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={backdatedForm.date}
                onChange={(e) => setBackdatedForm({ ...backdatedForm, date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                value={backdatedForm.time}
                onChange={(e) => setBackdatedForm({ ...backdatedForm, time: e.target.value })}
              >
                {TIME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Labor type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Labor Type</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={backdatedForm.laborType}
              onChange={(e) => setBackdatedForm({ ...backdatedForm, laborType: e.target.value })}
            >
              {USER_LABOR_TYPES.map((lt) => (
                <option key={lt.value} value={lt.value}>{lt.label}</option>
              ))}
              {/* V2-0 K2/A6: design labor bookable on any job. */}
              {isAdmin && (
                <optgroup label="Design Labor">
                  {ADMIN_DESIGN_LABOR_TYPES.map((lt) => (
                    <option key={lt.value} value={lt.value}>{lt.label}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="ghost" size="sm" onClick={() => setBackdatedModalOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              loading={backdatedLoading}
              onClick={async () => {
                setBackdatedLoading(true);
                try {
                  const customStartTime = `${backdatedForm.date}T${backdatedForm.time}:00`;
                  await onClockToggle?.(job, backdatedForm.laborType, customStartTime);
                  setBackdatedModalOpen(false);
                } catch (err) {
                  alert(err?.response?.data?.error || 'Clock-in failed');
                } finally {
                  setBackdatedLoading(false);
                }
              }}
            >
              Clock In (Backdated)
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Builder initials with live running timer ──
function BuilderTimer({ builder }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!builder.clock_in_at) return;
    const start = new Date(builder.clock_in_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [builder.clock_in_at]);

  const hrs = Math.floor(elapsed / 3600);
  const mins = Math.floor((elapsed % 3600) / 60);
  const secs = elapsed % 60;
  const timerStr = `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

  return (
    <div
      className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-50 border border-green-200"
      title={`${builder.name} — clocked in`}
    >
      <div className="w-5 h-5 rounded-full bg-green-500 text-white text-[8px] font-bold flex items-center justify-center flex-shrink-0">
        {builder.initials}
      </div>
      <span className="text-[11px] font-mono font-bold text-green-700">{timerStr}</span>
    </div>
  );
}
